import axios from "axios";
import { getDevelopmentBearerToken } from "./authSession";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Resolve the device timezone once at module load (Intl.DateTimeFormat is
 * cheap, but we cache the value so the interceptor doesn't reach into Intl
 * on every request). Falls back to undefined on browsers that don't expose
 * a resolved timezone — backend then uses its own configured fallback
 * (`SHELFLIFE_DEFAULT_TIMEZONE`, default America/New_York).
 */
const DEVICE_TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
})();

const api = axios.create({ baseURL: API_BASE, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = getDevelopmentBearerToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Pass the device timezone on every request as a query param. Endpoints
  // that honor it (e.g. /inventory) use it to compute urgency by *local*
  // calendar date instead of UTC. Endpoints that don't read it simply
  // ignore the unused query string — no risk to existing routes.
  if (DEVICE_TZ) {
    config.params = { tz: DEVICE_TZ, ...(config.params || {}) };
  }
  return config;
});

export function formatApiErrorDetail(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}

/**
 * True iff this looks like an offline / network failure — i.e. no HTTP
 * response made it back to the client. Use this BEFORE surfacing red
 * error toasts, so we don't tell employees "Network Error" when their
 * device is simply offline (which they already know).
 */
export function isNetworkError(err) {
  if (!err) return false;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  if (err.response) return false;          // server actually replied
  const msg = String(err.message || "").toLowerCase();
  if (msg.includes("network") || msg.includes("failed to fetch")) return true;
  if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") return true;
  return false;
}

export default api;
